import { RiskFactorAction, useGetSpaceRiskFactorsQuery } from "~/operations";
import { Space } from "~/lib/types";
import { Box, Button, Divider, Grid, Link, Typography } from "@mui/material";
import { docsHref } from "~/lib/docs-href";
import { Loading } from "~/components/loading";
import { useState } from "react";
import Breadcrumbs, { Breadcrumb } from "~/components/Breadcrumbs/Breadcrumbs";
import {
  fieldId,
  formatMagnitude,
  getInitialCondition,
  RiskFactorConditionKeyValueField,
  RiskFactorConditionKeyValueOperator,
  RiskFactorConditionOperator,
  RiskFactorsConfigurationForm,
  RiskFactorsConfigurationInput,
} from "~/pages/security-model/RiskFactorsConfiguration/ConfigurationForm/RiskFactorsConfigurationForm";
import { setDocumentTitle } from "~/lib/commonUtils";

const breadcrumbs: Breadcrumb[] = [
  { title: "Security Model", key: "security-model" },
  { title: "Risk Factors Configuration", key: "risk-factors" },
];

type Props = {
  space: Space;
};

export function RiskFactorsConfiguration({ space }: Props) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const spaceMrn = space.mrn;

  const { data, loading } = useGetSpaceRiskFactorsQuery({
    variables: { spaceMrn },
    fetchPolicy: "network-only",
  });

  const riskFactors = data?.riskFactors.edges || [];
  // sort the riskFactors so that tagged risk factors are first in the list, High Prio then Low Prio.
  const highPrio = riskFactors.find(
    (riskFactor) => riskFactor.title === "High-priority asset",
  );
  const lowPrio = riskFactors.find(
    (riskFactor) => riskFactor.title === "Low-priority asset",
  );
  if (lowPrio) {
    riskFactors.splice(riskFactors.indexOf(lowPrio), 1);
    //place low priority asset after high priority asset
    riskFactors.unshift(lowPrio);
  }
  if (highPrio) {
    riskFactors.splice(riskFactors.indexOf(highPrio), 1);
    riskFactors.unshift(highPrio);
  }

  const defaultValues =
    riskFactors.reduce<RiskFactorsConfigurationInput>(
      (acc, { mrn, action, magnitude, assetFieldFilters }) => {
        const { value, isToxic } = magnitude;
        const rawMagnitude = formatMagnitude(value);

        const labelConditions =
          assetFieldFilters?.labelBasedFilters?.flatMap((f) => {
            return {
              formKey: RiskFactorConditionKeyValueField.Labels,
              operator: RiskFactorConditionOperator.Or,
              keyValueCondition: {
                field: RiskFactorConditionKeyValueField.Unknown,
                operator: RiskFactorConditionKeyValueOperator.Contains,
                values: [
                  {
                    key: f.key,
                    value: f.value,
                  },
                ],
              },
            };
          }) || [];

        const annotationConditions =
          assetFieldFilters?.annotationBasedFilters?.flatMap((f) => {
            return {
              formKey: RiskFactorConditionKeyValueField.Annotations,
              operator: RiskFactorConditionOperator.Or,
              keyValueCondition: {
                field: RiskFactorConditionKeyValueField.Unknown,
                operator: RiskFactorConditionKeyValueOperator.Contains,
                values: [
                  {
                    key: f.key,
                    value: f.value,
                  },
                ],
              },
            };
          }) || [];

        const conditions = [...labelConditions, ...annotationConditions];

        const isTaggable = assetFieldFilters !== null;

        acc[fieldId(mrn)] = {
          enabled: action === RiskFactorAction.Enable,
          magnitude: value,
          rawMagnitude,
          isToxic,
          selections: isTaggable
            ? [
                {
                  conditions: [
                    ...(conditions.length > 0
                      ? conditions
                      : [getInitialCondition()]),
                  ],
                },
              ]
            : undefined,
        };
        return acc;
      },
      {},
    ) || {};

  setDocumentTitle(["Risk Factors", "Security Model", space.name]);

  return (
    <Box>
      <Breadcrumbs items={breadcrumbs} />
      <Grid
        container
        columns={1}
        columnGap={2}
        mt={2}
        mb={4}
        sx={{
          alignItems: {
            xs: "start",
            sm: "center",
          },
        }}
      >
        <Grid sx={{ width: "100%" }}>
          <Typography
            component="h1"
            fontWeight="bold"
            variant="h4"
            textTransform="uppercase"
            sx={{
              fontSize: {
                xs: 22,
                sm: 32,
              },
              mb: 3,
            }}
          >
            Risk Factors Configuration
          </Typography>

          <Grid
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "flex-start" },
              gap: { xs: 2, md: 0 },
            }}
          >
            <Grid>
              <Typography variant="body1" gutterBottom>
                Choose which factors affect how Mondoo evaluates your assets'
                risk scores.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "text.warning",
                }}
              >
                WARNING: Adjusting these risk factors will change the scores on
                your assets.
              </Typography>
              <Typography variant="body1">
                To learn more, read the{" "}
                <Link
                  href={docsHref("/platform/security/customize/risk/")}
                  color="primary"
                  target="_blank"
                >
                  Mondoo documentation
                </Link>
                .
              </Typography>
            </Grid>
            {!loading && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setShowAdvanced(!showAdvanced)}
                sx={{ alignSelf: { xs: "flex-start", md: "flex-start" } }}
              >
                {showAdvanced ? "Hide" : "Show"} Advanced Settings
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Divider
        flexItem
        sx={{ display: { xs: "none", md: "flex" }, my: 1, height: 1 }}
      />

      {loading && (
        <Box mt={8}>
          <Loading what="Risk Factors" />
        </Box>
      )}

      {!loading && riskFactors.length > 0 && (
        <RiskFactorsConfigurationForm
          space={space}
          riskFactors={riskFactors}
          defaultValues={defaultValues}
          showAdvanced={showAdvanced}
        />
      )}
    </Box>
  );
}
